import {createContext, useCallback, useContext, useMemo, useState} from 'react';

import {AttributeType} from './attributes-type';

type AttributesStateType = Map<string, AttributeType>;

type AttributesContextType = {
    attributesState: AttributesStateType;
    updateAttribute: (internalName: string, attribute: AttributeType) => void;
    getAttributes: () => AttributesStateType;
    clearAttributes: () => void;
};

const AttributesContext = createContext<AttributesContextType | null>(null);

type PropsType = {
    children: React.ReactNode;
};

export function AttributesStateProvider(props: PropsType): JSX.Element {
    const {children} = props;
    const [attributesState, setAttributesState] = useState<AttributesStateType>(new Map());

    const updateAttribute = useCallback((internalName: string, attribute: AttributeType) => {
        setAttributesState((previousState) => {
            const newState = new Map(previousState);

            newState.set(internalName, attribute);

            return newState;
        });
    }, []);

    const getAttributes = useCallback(() => {
        return attributesState;
    }, [attributesState]);

    const clearAttributes = useCallback(() => {
        setAttributesState(new Map());
    }, []);

    const value = useMemo(
        () => ({
            attributesState,
            updateAttribute,
            getAttributes,
            clearAttributes,
        }),
        [attributesState, updateAttribute, getAttributes, clearAttributes]
    );

    return <AttributesContext.Provider value={value}>{children}</AttributesContext.Provider>;
}

export function useAttributesState(): AttributesContextType {
    const context = useContext(AttributesContext);

    if (!context) {
        throw new Error('useAttributesState must be used within AttributesStateProvider');
    }

    return context;
}
